// Approving a self-registered OAuth client (#135): an app that names itself with
// a Client ID Metadata Document URL rather than being created by an admin, such
// as Claude connecting to an MCP server. The API answers the first authorize
// post with what to ask the user; the view posts again with their answer. Kept
// pure so the wording decisions can be tested without a browser.
//
// Not the document-acknowledgement consent in consents.js -- that one is about
// terms the user agrees to, this one is about an app the user lets in.

export const CLIENT_CONSENT_GRANTED = 'granted';
export const CLIENT_CONSENT_DENIED = 'denied';

// OIDC scopes say nothing about what the app may do at the API, so they are
// left out of the list the user is asked to approve.
const OIDC_SCOPES = new Set(['openid', 'profile', 'email', 'offline_access']);

/**
 * The JSON body for POST /api/v1/authorize.
 * @param {import('./pending-oauth.js').PendingOAuth} params
 * @param {string} [consent] CLIENT_CONSENT_GRANTED or CLIENT_CONSENT_DENIED once the user has answered
 * @returns {object}
 */
export function authorizeRequestBody(params, consent) {
    const body = {
        client_id: params.clientId,
        redirect_uri: params.redirectUri,
        response_type: params.responseType,
        scope: params.scope,
        state: params.state,
        audience: params.audience,
        resource: params.resource || [],
        nonce: params.nonce,
        code_challenge: params.codeChallenge,
        code_challenge_method: params.codeChallengeMethod,
        tenant_slug: params.tenantSlug,
    };
    if (consent) body.consent = consent;
    return body;
}

/**
 * What the consent card shows, from the API's consent_required answer.
 *
 * The app is named by the host of its client_id URL, because that host is the
 * one thing about it the API verified -- it served the metadata document. The
 * name in the document is the app's own claim, so it is shown as "calls itself"
 * and only when it says something the host doesn't.
 * @param {object} prompt the API's consent_required response
 * @returns {{clientHost: string, clientName: string, showClientName: boolean, apiName: string, scopes: string[], redirectHost: string, loopbackOnly: boolean}}
 */
export function clientConsentView(prompt) {
    const clientHost = prompt.client?.host || '';
    const clientName = (prompt.client?.name || '').trim();

    return {
        clientHost,
        clientName,
        showClientName: clientName !== '' && clientName.toLowerCase() !== clientHost.toLowerCase(),
        apiName: prompt.resource?.name || prompt.resource?.identifier || '',
        scopes: (prompt.scopes || []).filter((scope) => scope && !OIDC_SCOPES.has(scope)),
        redirectHost: prompt.redirect_host || '',
        loopbackOnly: prompt.loopback_only === true,
    };
}
